import React, { useEffect, useState } from 'react';
import {
  Grid,
  IconButton,
  Paper,
  ClickAwayListener,
  Popper,
  PopperPlacementType,
  Fade,
  Theme,
  useTheme,
  ArrowForward as ArrowForwardIcon,
  makeStyles,
  createStyles,
} from '@quillbot/design-system';
import logger from 'core/logger/logger';
import { useSelector } from 'react-redux';
import commonTrack from '../handlers/track';
import {
  scrapeArticleFromURL,
  scrapeCaptionsFromYoutube,
} from '../handlers/api';
import { RootState } from '../models/redux/store';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      // backgroundColor: theme.palette.background.paper,
    },
    container: {
      width: '100%',
      // height: '75.12px',
      // background: '#FFFFFF',
      boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.03)',
      borderRadius: '3px',
    },
    innerContainer: {
      border: `1px solid ${theme.palette.divider}`,
      outline: 'none',
      boxSizing: 'border-box',
      borderRadius: '4px',
      '&:focus-within': {
        border: '1px solid ',
        borderColor: theme.palette.primary.main,
      },
      // margin: 10,
      marginBottom: '5px',
      padding: '0px 9px',
    },
    InvalidLinkBorder: {
      '&:focus-within': {
        border: `1px solid ${theme.palette.error.main}`,
      },
    },
    inputBox: {
      fontFamily: 'Open Sans',
      background: theme.palette.grey[50],
      border: 'none',
      boxSizing: 'border-box',
      borderRadius: '4px',
      outline: 'none',
      paddingLeft: '4px',
      color: theme.palette.text.primary,

      // '&:focus': {
      //   border: '1px solid #4C9459',
      // },
      //
      '&::-webkit-input-placeholder': {
        color: theme.palette.text.secondary,
      },
      '&:-moz-placeholder': {
        color: theme.palette.text.secondary,
      },
      '&::-moz-placeholder': {
        color: theme.palette.text.secondary,
      },
      '&:-ms-input-placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    nextIcon: {
      position: 'absolute',
      top: '21px',
      right: '20px',
      // height: '30px',
      // width: '30px',
      padding: '1px',
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
      // marginRight: '3px',
    },
    arrowIcon: {
      color: theme.palette.primary.contrastText,
      '&:hover': {
        color: theme.palette.primary.contrastText,
      },
    },
    arrow: {
      overflow: 'hidden',
      position: 'absolute',
      width: '22px',
      height: '16px' /* = width / sqrt(2) = (length of the hypotenuse) */,
      boxSizing: 'border-box',
      color: theme.palette.grey[50],
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        boxShadow: theme.shadows[1],
        backgroundColor: 'currentColor',
        transform: 'rotate(45deg)',
      },
    },
    popper: {
      '&[x-placement*="bottom"] $arrow': {
        top: 0,
        left: 0,
        marginTop: '-1.16em',
        marginLeft: 4,
        marginRight: 4,
        '&::before': {
          transformOrigin: '0 100%',
        },
      },
      '&[x-placement*="top"] $arrow': {
        bottom: '-2px',
        left: 0,
        marginBottom: '-0.9em',
        marginLeft: 4,
        marginRight: 4,
        transform: 'rotate(-180deg)',
        '&::before': {
          transformOrigin: '0 100%',
          transform: 'rotate(45deg)',
        },
      },
      boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
      borderRadius: '3px',
    },
    popperForMobile: {
      top: '-30px !important',
    },
    popperForPc: {
      top: '21px !important',
    },
  })
);

interface IProps {
  anchorEl: null | HTMLElement;
  scrapeUrl: Function;
  clickAwayHandler: Function;
  resetUrlPopperHelperText: Function;
  inputBoxEl: null | HTMLElement;
  scraperType: String;
  urlPopperHelpText: String;
  shouldShowInRed: boolean;
  placement?: PopperPlacementType;
}

const UrlScrapperInputPopper = ({
  anchorEl,
  scrapeUrl,
  clickAwayHandler,
  resetUrlPopperHelperText,
  inputBoxEl,
  scraperType,
  urlPopperHelpText,
  shouldShowInRed,
  placement,
}: IProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const [url, setUrl] = useState('');
  const isMobile = useSelector(
    (state: RootState) => state.common.platform.isMobile
  );
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);
  useEffect(() => {
    setUrl('');
  }, [anchorEl]);

  const showType = () => scraperType;

  const calculateWidth = () => {
    if (inputBoxEl) {
      if (inputBoxEl.clientWidth < 380) {
        return inputBoxEl.clientWidth - 30;
      }
      return 369;
    }
    return 0;
  };
  const isValidHttpUrl = (string: string) => {
    let tempUrl;
    let validatesWithRegEx = false;
    try {
      tempUrl = new URL(string);
      // original
      // const pattern = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
      const pattern =
        /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
      validatesWithRegEx = !!pattern.test(string);
    } catch (err) {
      return false;
    }
    return (
      (tempUrl.protocol === 'http:' || tempUrl.protocol === 'https:') &&
      validatesWithRegEx
    );
  };

  const isValidYoutubeUrl = (string: string) => {
    let tempUrl;
    let validatesWithRegEx = false;
    try {
      tempUrl = new URL(string);
      const pattern = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/g;
      validatesWithRegEx = !!pattern.test(string);
    } catch (err) {
      return false;
    }
    return (
      (tempUrl.protocol === 'http:' || tempUrl.protocol === 'https:') &&
      validatesWithRegEx
    );
  };

  const scrapeUrlHandler = () => {
    const scrappedResultPromise = new Promise((resolve, reject) =>
      scrapeArticleFromURL({ url })
        .then((res: { text: string }) => {
          const inputBox = document.createElement('input');
          const htmlDocument = document.createElement('div');
          htmlDocument.innerHTML = res.text;
          const images = document.getElementsByTagName('img');
          const length = images.length;
          for (let i = 0; i < length; i += 1) {
            if (images[0] && images[0].parentNode) {
              images[0].parentNode.removeChild(images[0]);
            }
          }
          inputBox.textContent = htmlDocument.innerHTML;
          commonTrack.initiatedUrlScrapping(url, true);
          resolve(
            inputBox.textContent.length > 0
              ? inputBox.textContent
              : 'No captions found!'
          );
        })
        .catch((err) => {
          commonTrack.initiatedUrlScrapping(url, false);
          reject();
          logger('Error in URL scraping', err);
        })
    );
    scrapeUrl(scrappedResultPromise, url);
  };

  // const scrapeUrlHandler = () => {
  //   const scrappedResultPromise = new Promise((resolve, reject) =>
  //     scrapeArticleFromURL({ url })
  //       .then((res: { text: string }) => {
  //         const inputBox = document.createElement('input');
  //         const htmlDocument = document.createElement('div');
  //         htmlDocument.innerHTML = res.text;
  //         const images = document.getElementsByTagName('img');
  //         const length = images.length;
  //         for (let i = 0; i < length; i += 1) {
  //           if (images[0] && images[0].parentNode) {
  //             images[0].parentNode.removeChild(images[0]);
  //           }
  //         }
  //         inputBox.textContent = htmlDocument.innerHTML;
  //         commonTrack.initiatedUrlScrapping(url, true);
  //         resolve(inputBox.textContent.length > 0 ? inputBox.textContent : 'No data found!');
  //       })
  //       .catch((err) => {
  //         commonTrack.initiatedUrlScrapping(url, false);
  //         reject();
  //         logger('Error in fetching data', err);
  //       })
  //   );
  //   scrapeUrl(scrappedResultPromise, url);
  // };

  const youTubeGetId = (link: any) => {
    // Getting YouTube video Id
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = link.match(regExp);
    if (match && match[7].length === 11) {
      return match[7];
    }
    return null;
  };
  const average = (data: any) => {
    const sum = data.reduce((sum1: any, value: any) => sum1 + value, 0);

    const avg = sum / data.length;
    return avg;
  };
  const standardDeviation = (values: number[]) => {
    const avg = average(values);

    const squareDiffs = values.map((value: number) => {
      const diff = value - avg;
      const sqrDiff = diff * diff;
      return sqrDiff;
    });

    const avgSquareDiff = average(squareDiffs);

    const stdDev = Math.sqrt(avgSquareDiff);
    return stdDev;
  };

  const getGaps = (captions: any) => {
    let i = -1;
    let gap = 0;
    const allGaps = captions.map((e: any) => {
      i += 1;
      gap =
        i > 0
          ? e.start - (captions[i - 1].start + captions[i - 1].duration)
          : 0;

      if (i > 0 && gap > 0) {
        return gap;
      }

      return 0;
    });
    return allGaps;
  };

  const captionSanitizer = (captions: any, isAutoGenerated: boolean) => {
    const allGaps = getGaps(captions);
    let i = -1;
    const threshold = standardDeviation(allGaps);
    const newLine = '\n';
    const newPara = '\n \n';

    const allText = captions.map((e: { start: number; text: string }) => {
      i += 1;
      let temp = null;
      if (!isAutoGenerated) {
        if (
          i > 0 &&
          e.start - (captions[i - 1].start + captions[i - 1].duration) >
            threshold
        ) {
          temp = newPara + e.text;
        } else {
          temp = e.text;
        }
      } else if (isAutoGenerated) {
        if (
          i > 0 &&
          e.start - (captions[i - 1].start + captions[i - 1].duration) >
            threshold
        ) {
          temp = newPara + e.text;
        } else if (
          i > 0 &&
          e.start - (captions[i - 1].start + captions[i - 1].duration) > 0
        ) {
          temp = newLine + e.text;
        } else {
          temp = e.text;
        }
      }
      return temp;
    });
    const combinedText = allText
      .join(' ')
      .replace(/(\[[^[]*\] )|(\[[^[]*\])/g, '');
    // To remove patterns like [Music], [Applause],etc.
    return combinedText.trim();
  };

  const scrapeYoutubeUrlHandler = () => {
    // Fetching YouTube video captions
    const videoId = youTubeGetId(url);
    const scrappedResultPromise = new Promise((resolve, reject) =>
      scrapeCaptionsFromYoutube(videoId)
        .then((res: any) => {
          const captionData = res.captions;
          const isAutoGenerated = res.autoGenerated;
          const textData =
            captionData.length > 0
              ? captionSanitizer(captionData, isAutoGenerated)
              : null;
          commonTrack.youtubeScrapeStatus(url, 'SUCCESS', isAutoGenerated);
          resolve(textData?.length > 0 ? textData : 'No captions found!');
        })
        .catch((err) => {
          commonTrack.youtubeScrapeStatus(url, 'FAILURE', false);
          reject();
          logger('Error in fetching data', err);
        })
    );
    scrapeUrl(scrappedResultPromise, url);
  };

  const setPlacement = () => {
    if (placement) return placement;
    if (isMobile) return 'top';
    return 'bottom-end';
  };

  return (
    <Popper
      open={Boolean(anchorEl)}
      className={`${classes.popper} ${
        isMobile || placement ? classes.popperForMobile : classes.popperForPc
      }`}
      anchorEl={anchorEl}
      // placement={calculateWidth() < 300 ? 'top-end' : 'top'}
      placement={setPlacement()}
      // @ breaking
      modifiers={[
        {
          name: 'arrow',
          enabled: true,
          options: {
            element: arrowRef,
          },
        },
      ]}
      // modifiers={{
      //   arrow: {
      //     enabled: true,
      //     element: arrowRef,
      //   },
      // }}
      // onBlur={this.legendClick}
      style={{ zIndex: 10, left: '100px' }}
      transition
    >
      {(...TransitionProps) => (
        <Fade {...TransitionProps} timeout={350}>
          <div>
            <ClickAwayListener
              onClickAway={() => {
                clickAwayHandler();
              }}
            >
              <Paper
                style={{ marginBottom: '0px', boxShadow: theme.shadows[3] }}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <span className={classes.arrow} ref={setArrowRef} />
                  <Grid item style={{ padding: '11px', paddingBottom: '5px' }}>
                    <div className={classes.container}>
                      <div
                        className={`${classes.innerContainer} ${
                          (!isValidYoutubeUrl(url) &&
                            showType() === 'youTube' &&
                            url.length > 10) ||
                          shouldShowInRed
                            ? classes.InvalidLinkBorder
                            : null // Shows green border only if its a valid link
                        } ${
                          !isValidHttpUrl(url) && showType() === 'default'
                            ? classes.InvalidLinkBorder
                            : null // Shows green border only if its a valid link
                        }`}
                        style={{
                          width: calculateWidth(),
                        }}
                      >
                        <input
                          type="text"
                          className={classes.inputBox}
                          value={url}
                          onChange={(e) => {
                            setUrl(e.target.value);
                            resetUrlPopperHelperText();
                          }}
                          style={{
                            width: calculateWidth() - 50,
                            maxWidth: calculateWidth() - 50,
                            height: 45,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          placeholder="Enter link"
                        />

                        <IconButton
                          className={classes.nextIcon}
                          onClick={
                            showType() === 'youTube'
                              ? scrapeYoutubeUrlHandler
                              : scrapeUrlHandler
                          }
                          disabled={
                            urlPopperHelpText === 'Retrieving video caption...'
                          }
                          style={{
                            visibility:
                              url.length > 0 &&
                              ((isValidYoutubeUrl(url) &&
                                showType() === 'youTube') ||
                                (isValidHttpUrl(url) &&
                                  showType() === 'default'))
                                ? 'visible'
                                : 'hidden',
                          }}
                          size="large"
                        >
                          <ArrowForwardIcon className={classes.arrowIcon} />
                        </IconButton>
                      </div>
                    </div>
                    <span
                      style={{
                        // display: 'block',
                        fontFamily: 'Open Sans',
                        // fontStyle: 'italic',
                        fontWeight: 'normal',
                        fontSize: '12px',
                        color: shouldShowInRed
                          ? theme.palette.error.main
                          : theme.palette.text.secondary,
                        paddingLeft: '2px',
                        lineHeight: '16.34px',
                      }}
                    >
                      {urlPopperHelpText}
                    </span>
                  </Grid>
                  {/* {url.length > 0 && isValidHttpUrl(url) && (
                <IconButton
                  classes={{ root: classes.nextIcon }}
                  onClick={() => {
                    scrapeUrl(url);
                  }}>
                  <ArrowForwardIcon />
                </IconButton>
              )} */}
                </Grid>
              </Paper>
            </ClickAwayListener>
          </div>
        </Fade>
      )}
    </Popper>
  );
};

export default UrlScrapperInputPopper;
